import React, { useState } from "react";
import { Avatar } from "../../components/ui/Avatar";
import { Badge } from "../../components/ui/Badge";
import { PillButton } from "../../components/ui/PillButton";
import { Header } from "../../components/chat/Header";
import { WelcomeScreen } from "../../components/chat/WelcomeScreen";
import { UserMessage } from "../../components/messages/UserMessage";
import { TextCard } from "../../components/messages/cards/TextCard";
import { WayfindingCard } from "../../components/messages/cards/WayfindingCard";
import { RegistrationCard } from "../../components/messages/cards/RegistrationCard";
import { SessionsCard } from "../../components/messages/cards/SessionsCard";
import { ArticlesCard } from "../../components/messages/cards/ArticlesCard";
import { ExecutivesCard } from "../../components/messages/cards/ExecutivesCard";
import { PrizeCard } from "../../components/messages/cards/PrizeCard";
import { QuestionSingleResponseCard } from "../../components/messages/cards/QuestionSingleResponseCard";

const Section: React.FC<{ title: string; children: React.ReactNode }> = (
  { title, children },
) => (
  <div className="mb-10">
    <h2 className="text-navy font-bold text-base uppercase tracking-widest mb-4 pb-2 border-b border-grey-91">
      {title}
    </h2>
    {children}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = (
  { label, children },
) => (
  <div className="mb-6">
    <p className="text-navy-64 text-xs mb-2">{label}</p>
    {children}
  </div>
);

export const MocksPage: React.FC = () => {
  const [pillSelected, setPillSelected] = useState(false);

  return (
    <div className="bg-grey-94 min-h-screen">
      {/* Sticky mock header */}
      <div className="sticky top-0 z-20 bg-navy px-4 py-2 flex items-center justify-between">
        <span className="text-white font-bold text-sm tracking-wide">
          Component Gallery
        </span>
        <a href="/" className="text-coral text-xs font-semibold">
          ← Back to chat
        </a>
      </div>

      <div className="max-w-md mx-auto px-4 py-8">
        {/* ── UI Primitives ── */}
        <Section title="UI Primitives">
          <Row label="Avatar">
            <Avatar />
          </Row>
          <Row label="Badge variants">
            <div className="flex gap-2 flex-wrap">
              <Badge text="DAY 1" />
              <Badge text="DAY 2" />
              <Badge text="ONBOARDING" />
            </div>
          </Row>
          <Row label="PillButton">
            <div className="flex gap-2 flex-wrap">
              <PillButton
                label="Maya"
                onClick={() => setPillSelected(true)}
                selected={pillSelected}
              />
              <PillButton label="Daniel" onClick={() => {}} />
              <PillButton label="Priya" onClick={() => {}} />
            </div>
          </Row>
        </Section>

        {/* ── Chat Shell ── */}
        <Section title="Chat Shell">
          <Row label="Header (sticky in real app)">
            <div className="relative rounded-xl overflow-hidden border border-grey-91">
              <Header />
            </div>
          </Row>
          <Row label="WelcomeScreen">
            <div className="h-96 bg-grey-94 rounded-xl border border-grey-91 overflow-hidden flex flex-col">
              <WelcomeScreen />
            </div>
          </Row>
          <Row label="MessageInput">
            <div className="relative bg-white rounded-xl border border-grey-91 overflow-hidden">
              <div className="px-4 py-3 flex items-center gap-2">
                <input
                  disabled
                  placeholder="Type a message..."
                  className="flex-1 bg-grey-99 border border-grey-91 rounded-pill px-4 py-2 text-sm text-navy placeholder:text-navy-64 focus:outline-none"
                />
                <button className="w-9 h-9 bg-coral rounded-pill flex items-center justify-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="19" x2="12" y2="5" />
                    <polyline points="5 12 12 5 19 12" />
                  </svg>
                </button>
              </div>
            </div>
          </Row>
        </Section>

        {/* ── Messages ── */}
        <Section title="Messages">
          <Row label="UserMessage">
            <UserMessage message="Northline Health · VP of Digital" />
          </Row>
          <Row label="TextCard (plain assistant message)">
            <div className="flex items-start gap-2">
              <Avatar />
              <TextCard
                message={"Great to meet you, Maya.\n\nTo recommend the most relevant sessions and case studies for your goals — what's your company and job title?"}
              />
            </div>
          </Row>
        </Section>

        {/* ── Rich Cards ── */}
        <Section title="Rich Cards">
          <Row label="WayfindingCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <WayfindingCard
                data={{
                  type: "wayfinding",
                  title: "AI IN DIAGNOSTICS",
                  rows: [
                    { label: "Time", value: "Tue, 4:15 PM" },
                    { label: "Room", value: "Hall B, Level 3" },
                    { label: "From our booth", value: "6 min walk" },
                  ],
                  instructions:
                    "Head left past the main escalators. I'll ping you ten minutes before it starts.",
                }}
              />
            </div>
          </Row>

          <Row label="RegistrationCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <RegistrationCard
                data={{
                  type: "registration_summary",
                  entry_number: "#4821",
                  email: "maya@northlinehealth.com",
                  raffle_date: "Wed, September 16",
                  message:
                    "You're all set — subscription and giveaway entry confirmed. Now let's make the most of Day 1 at 11th Annual Becker's IT + Revenue Cycle Conference.",
                }}
              />
            </div>
          </Row>

          <Row label="SessionsCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <SessionsCard
                data={{
                  type: "session_recommendations",
                  text:
                    "Perfect. Based on your role as VP of Digital, here are two sessions you shouldn't miss:",
                  sessions: [
                    {
                      time: "TUE · 10:30 AM",
                      location: "GRAND BALLROOM C",
                      title: "AI at the Bedside: Scaling Clinical Copilots",
                      type: "Panel",
                      duration: "4 speakers",
                    },
                    {
                      time: "TUE · 2:00 PM",
                      location: "STAGE 2",
                      title: "The Digital Front Door Patients Actually Use",
                      type: "Keynote",
                      duration: "45 min",
                    },
                  ],
                }}
              />
            </div>
          </Row>

          <Row label="PrizeCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <PrizeCard
                data={{
                  type: "prize_cards",
                  text:
                    "One more thing before we jump in — enter to win a CI&T giveaway. Pick your preferred prize:",
                  prizes: [
                    {
                      id: "oura_ring",
                      label: "PRIZE A",
                      name: "Oura Ring",
                      description: "Smart health & wellness tracker",
                    },
                    {
                      id: "data_assessment",
                      label: "PRIZE B",
                      name: "Enterprise Data & AI Assessment",
                      description: "A working session with our strategy team",
                    },
                  ],
                }}
                onSelect={() => {}}
              />
            </div>
          </Row>

          <Row label="QuestionSingleResponseCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <QuestionSingleResponseCard
                data={{
                  type: "question_single_response",
                  text: "Answer yes or no:",
                  options: [
                    { id: "yes", label: "Yes" },
                    { id: "no", label: "No" },
                  ],
                }}
                onSelect={() => {}}
              />
            </div>
          </Row>

          <Row label="ArticlesCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <ArticlesCard
                data={{
                  type: "text_with_articles",
                  text:
                    "Based on your role, here are some relevant insights from CI&T:",
                  articles: [
                    {
                      title: "Unifying IT data sources into one patient record",
                      summary:
                        "How health systems are eliminating data silos to build a 360° view of the patient journey.",
                      url: "https://ciandt.com",
                    },
                    {
                      title: "The Physician Adoption Gap",
                      summary:
                        "Why 70% of clinical AI tools fail at the bedside — and what CI&T's delivery model does differently.",
                      url: "https://ciandt.com",
                    },
                  ],
                }}
              />
            </div>
          </Row>

          <Row label="ExecutivesCard">
            <div className="flex items-start gap-2">
              <Avatar />
              <ExecutivesCard
                data={{
                  type: "executives",
                  text:
                    "The CFO saw AI in RCX — here are the CI&T executives you should connect with:",
                  executives: [
                    {
                      name: "Alex Moreira",
                      role: "VP of Healthcare Innovation",
                      linkedin_url: "https://linkedin.com",
                      image_url:
                        "https://becker-dev.infinity-copilot.com/execs/cieslak.png",
                    },
                    {
                      name: "Renata Cole",
                      role: "Director of Digital Health",
                      linkedin_url: "https://linkedin.com",
                    },
                  ],
                }}
              />
            </div>
          </Row>
        </Section>

        {/* ── Full Conversation Simulation ── */}
        <Section title="Full Conversation Simulation">
          <div className="flex flex-col gap-4 bg-grey-94 rounded-xl p-4 border border-grey-91">
            <div className="flex items-start gap-2">
              <Avatar />
              <TextCard message="Welcome to CI&T at 11th Annual Becker's IT + Revenue Cycle Conference. We're here to help you create experiences that patients and providers fall in love with. To get started — what should we call you?" />
            </div>

            <UserMessage message="Maya" />

            <div className="flex items-start gap-2">
              <Avatar />
              <TextCard message="Great to meet you, Maya. To recommend the most relevant sessions and case studies for your goals — what's your company and job title?" />
            </div>

            <UserMessage message="Northline Health · VP of Digital" />

            <div className="flex items-start gap-2">
              <Avatar />
              <SessionsCard
                data={{
                  type: "session_recommendations",
                  text:
                    "Perfect. Based on your role as VP of Digital, here are two sessions you shouldn't miss:",
                  sessions: [
                    {
                      time: "TUE · 10:30 AM",
                      location: "GRAND BALLROOM C",
                      title: "AI at the Bedside: Scaling Clinical Copilots",
                      type: "Panel",
                      duration: "4 speakers",
                    },
                    {
                      time: "TUE · 2:00 PM",
                      location: "STAGE 2",
                      title: "The Digital Front Door Patients Actually Use",
                      type: "Keynote",
                      duration: "45 min",
                    },
                  ],
                }}
              />
            </div>

            <UserMessage message="Added to my agenda" />

            <div className="flex items-start gap-2">
              <Avatar />
              <PrizeCard
                data={{
                  type: "prize_cards",
                  text:
                    "One more thing before we jump in — enter to win a CI&T giveaway. Pick your preferred prize:",
                  prizes: [
                    {
                      id: "oura_ring",
                      label: "PRIZE A",
                      name: "Oura Ring",
                      description: "Smart health & wellness tracker",
                    },
                    {
                      id: "data_assessment",
                      label: "PRIZE B",
                      name: "Enterprise Data & AI Assessment",
                      description: "A working session with our strategy team",
                    },
                  ],
                }}
                onSelect={() => {}}
              />
            </div>

            <UserMessage message="Oura Ring" />

            <div className="flex items-start gap-2">
              <Avatar />
              <RegistrationCard
                data={{
                  type: "registration_summary",
                  entry_number: "#4821",
                  email: "maya@northlinehealth.com",
                  raffle_date: "Wed, September 16",
                  message:
                    "You're all set — subscription and giveaway entry confirmed. Now let's make the most of Day 1 at 11th Annual Becker's IT + Revenue Cycle Conference.",
                }}
              />
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};
